import { useContext, useState, useRef, useEffect } from 'react';
import "./PasswordReset.scss";
import InputBox from '../../../components/Fields/Input Box/InputBox';
import { styles, labelStyles, inputStyles } from "./PasswordResetFieldStyles.js";
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { toast } from 'react-toastify';
import { AppContext } from '../../../context/AppContext.jsx';
import { InputText } from 'primereact/inputtext';

const PasswordReset = () => {

    const [email, setEmail] = useState("");
    const { backendUrl } = useContext(AppContext);
    const [isEmailSent, setIsEmailSent] = useState(false);
    const [isOtpSubmitted, setIsOtpSubmitted] = useState(false);
    const [otp, setOtp] = useState(0);
    const [newPassword, setNewPassword] = useState("");
    const navigate = useNavigate();
    const newPasswordRef = useRef(null);
    const emailInputRef = useRef(null);
    const inputRefs = useRef([]);
    const [otpTimer, setOtpTimer] = useState(300);
    const [startTimer, setStartTimer] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isValidatingOtp, setIsValidatingOtp] = useState(false);
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    useEffect(() => {
        if (!isEmailSent && emailInputRef.current) {
            emailInputRef.current.focus();
        }
    }, [isEmailSent]);

    useEffect(() => {
        if (isEmailSent && !isOtpSubmitted && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [isEmailSent, isOtpSubmitted]);

    useEffect(() => {
        if (isOtpSubmitted && isEmailSent && newPasswordRef.current) {
            newPasswordRef.current.focus();
        }
    }, [isOtpSubmitted]);

    useEffect(() => {
        let timerInterval;
        if (isEmailSent) {
            setOtpTimer(300);
            timerInterval = setInterval(() => {
                setOtpTimer(prev => {
                    if (prev <= 1) {
                        clearInterval(timerInterval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timerInterval);
    }, [isEmailSent, startTimer]);

    const validateInput = (text) => {
        const trimmedText = text.trim();
        if (!trimmedText) {
            return "Please enter your new password";
        }
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(trimmedText)) {
            return "Invalid Format";
        }
        return null;
    };

    const validateEmail = (text) => {
        const trimmedText = text.trim();
        if (!trimmedText) {
            return "Email is required";
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedText)) {
            return "Invalid email format";
        }
        return null;
    };


    async function onSubmitEmail(e) {
        e.preventDefault();
        const error = validateEmail(email);
        if (error) {
            setValidationError(error);
            return;
        }
        setIsSendingOtp(true);
        try {
            const response = await axios.post(`${backendUrl}/api/auth/sendResetOtp`, { email });
            const { data } = response;

            if (data.success) {
                toast.success(data.message || "OTP sent successfully");
                setIsEmailSent(true);
            }
            else {
                toast.error(data.message || "Failed to send OTP");
            }
        } catch (error) {
            if (error.response && error.response.status === 503) {
                toast.error("⚠️ The service is currently under maintenance. Please try again later.");
            }
            else if (!navigator.onLine) {
                toast.error("🚫 No internet connection. Please check your network.");
            }

            else if (error.request && !error.response) {
                toast.error("⚠️ Network issue or server is unreachable. Try again later.")
            }
            else {
                console.error("Unexpected error:", error);
                toast.error("Something went wrong.");
            }
        } finally {
            setIsSendingOtp(false);
        }
    }

    async function resendOtp(e) {
        e.preventDefault();
        setIsSendingOtp(true);
        try {
            const response = await axios.post(`${backendUrl}/api/auth/sendResetOtp`, { email });
            const { data } = response;

            if (data.success) {
                toast.success(data.message || "OTP sent successfully");
                setIsEmailSent(true);
                setStartTimer(!startTimer);
                setOtpTimer(300);
            } else {
                toast.error(data.message || "Failed to send OTP");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Something went wrong");
        } finally {
            setIsSendingOtp(false);
        }
    }

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
        const secs = (seconds % 60).toString().padStart(2, '0');
        return `${mins} minutes, ${secs} seconds`;
    };

    async function onSubmitOtp(e) {
        e.preventDefault();
        setIsValidatingOtp(true);
        try {
            const otpArray = inputRefs.current.map(e => e.value.trim());
            const enteredOtp = otpArray.join("");
            const isValidOtp = /^\d{6}$/.test(enteredOtp);
            if (!isValidOtp) {
                toast.error("Please enter a valid 6-digit OTP.");
                return;
            }
            const { data } = await axios.post(`${backendUrl}/api/auth/validateOtp`, {
                email,
                otp: enteredOtp
            });
            if (data.success) {
                toast.success(data.message);
                setOtp(enteredOtp);
                setIsOtpSubmitted(true);
            }
            else {
                toast.error(data.message || "Invalid OTP");
            }
        } catch (error) {
            if (error.response && error.response.status === 503) {
                toast.error("⚠️ The service is currently under maintenance. Please try again later.");
            }
            else if (!navigator.onLine) {
                toast.error("🚫 No internet connection. Please check your network.");
            }

            else if (error.request && !error.response) {
                toast.error("⚠️ Network issue or server is unreachable. Try again later.")
            }
            else {
                console.error("Unexpected error:", error);
                toast.error("Something went wrong.");
            }
        } finally {
            setIsValidatingOtp(false);
        }
    }

    async function onSubmitNewPassword(e) {
        e.preventDefault();
        const error = validateInput(newPassword);
        if (error) {
            setValidationError(error);
            return;
        }
        setIsResettingPassword(true);
        try {
            const { data } = await axios.post(`${backendUrl}/api/auth/resetPassword`, { email, otp, newPassword });
            if (data.success) {
                toast.success(data.message);
                navigate("/login");
            }
            else {
                toast.error(data.message);
            }
        } catch (error) {
            if (error.response && error.response.status === 503) {
                toast.error("⚠️ The service is currently under maintenance. Please try again later.");
            }
            else if (!navigator.onLine) {
                toast.error("🚫 No internet connection. Please check your network.");
            }

            else if (error.request && !error.response) {
                toast.error("⚠️ Network issue or server is unreachable. Try again later.")
            }
            else {
                console.error("Unexpected error:", error);
                toast.error("Something went wrong.");
            }
        } finally {
            setIsResettingPassword(false);
        }
    }

    const handleInput = (e, index) => {
        const value = e.target.value;
        if (!/^\d$/.test(value)) {
            e.target.value = "";
            return;
        }
        if (index < inputRefs.current.length - 1) {
            inputRefs.current[index + 1].focus();
        }
    };

    function handleKeyDown(e, index) {
        if (e.key === "Backspace" && e.target.value === "" && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    }

    function handlePaste(e) {
        const paste = e.clipboardData.getData("text");
        const pasteArray = paste.split("");
        pasteArray.forEach((char, index) => {
            if (inputRefs.current[index]) {
                inputRefs.current[index].value = char;
            }
        })
    }

    return (
        <div className="passwordResetPageMainContainer">
            {!isEmailSent && (
                <>
                    <form action="" className="inputBoxParent" onSubmit={onSubmitEmail}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            <InputBox
                                fieldName="email"
                                fieldType="email"
                                customLabelName="Email"
                                placeholder="enter your registered email"
                                styles={styles}
                                labelStyles={labelStyles}
                                inputStyles={inputStyles}
                                required={true}
                                value={email}
                                handleChange={(e) => {
                                    setEmail(e.target.value)
                                    validationError && setValidationError("");
                                }}
                                ref={emailInputRef}
                            />
                            {validationError && (
                                <div style={{ fontFamily: "var(--font-paragraph)", fontSize: "18px", color: "red", fontWeight: "500" }}>
                                    <p>{validationError}</p>
                                </div>
                            )}
                        </div>
                        <div className="btnsParens">
                            <Button
                                label="Cancel"
                                type="button"
                                className="cancelBtn btn"
                                onClick={() => navigate("/Login")}
                            />
                            <Button
                                label={isSendingOtp ? "Sending..." : "Send OTP"}
                                className="sendOtpBtn btn"
                                disabled={isSendingOtp}
                                icon={isSendingOtp ? "pi pi-spinner pi-spin" : ""}
                            />
                        </div>
                    </form>
                </>
            )}


            {!isOtpSubmitted && isEmailSent && (
                <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                    <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center" }}>
                        {otpTimer > 0 ? (
                            <>
                                <p className="timerText">Your OTP will be valid only for {formatTime(otpTimer)}.</p>
                                <p className="timerText">Do not refresh the page.</p>
                            </>
                        ) : (
                            <p className="timerText" style={{ color: "red", fontWeight: "bold" }}>
                                OTP is expired. Please click the "Resend OTP" button.
                            </p>
                        )}
                    </div>
                    <form className="resetOtpFormContainer" onSubmit={onSubmitOtp}>
                        <div className="headerParent">
                            <h2>Reset Password OTP</h2>
                            <p>Enter the 6-digit code that has been sent to your email</p>
                        </div>

                        <div className="otpInputParent">
                            {Array(6).fill(0).map((_, index) => (
                                <input
                                    type="text"
                                    maxLength="1"
                                    key={index}
                                    ref={(e) => inputRefs.current[index] = e}
                                    onPaste={handlePaste}
                                    onInput={(e) => handleInput(e, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    className="OtpInputBoxes"
                                />
                            ))}
                        </div>

                        <div className="btnParent">
                            <Button
                                type="button"
                                label={isSendingOtp ? "Resending..." : "Resend OTP"}
                                className="resendOtp btn"
                                onClick={resendOtp}
                                disabled={isSendingOtp || isValidatingOtp}
                                icon={isSendingOtp ? "pi pi-spinner pi-spin" : ""}
                            />
                            <Button
                                type="submit"
                                label={isValidatingOtp ? "Verifying..." : "Submit"}
                                className="sendOtpBtn btn"
                                disabled={isValidatingOtp || isSendingOtp}
                                icon={isValidatingOtp ? "pi pi-spinner pi-spin" : ""}
                            />
                        </div>
                    </form>

                </div>
            )}

            {/* Enter New Password */}
            {isOtpSubmitted && isEmailSent && (
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                    <form action="" onSubmit={onSubmitNewPassword} className="newPasswordFormContainer">
                        <div
                            style={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                position: "relative"
                            }}
                        >
                            <label
                                htmlFor="newPassword"
                                style={{
                                    ...labelStyles,
                                    marginBottom: '6px',
                                    fontWeight: '600'
                                }}
                            >
                                Enter the new password below
                            </label>
                            <InputText
                                id="newPassword"
                                ref={newPasswordRef}
                                value={newPassword}
                                type={showPassword ? 'text' : 'password'}
                                placeholder="enter your new password"
                                onChange={(e) => {
                                    setNewPassword(e.target.value);
                                    validationError && setValidationError("");
                                }}
                                required
                                autoComplete="off"
                                style={{
                                    ...inputStyles,
                                    width: '100%',
                                    paddingRight: '2.5rem',
                                }}
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={togglePasswordVisibility}
                                tabIndex={-1}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '72.5%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#6c757d',
                                    fontSize: '1.2rem',
                                    padding: '0.25rem',
                                    borderRadius: '50%',
                                    transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                                <i className={showPassword ? "pi pi-eye" : "pi pi-eye-slash"} />
                            </button>
                        </div>

                        {validationError && (
                            <div style={{ width: "100%" }}>
                                <p>{validationError}</p>
                            </div>
                        )}
                        <div className="btnParent">
                            <Button
                                label={isResettingPassword ? "Resetting..." : "Submit"}
                                className="submitBtn btn"
                                disabled={isResettingPassword}
                                icon={isResettingPassword ? "pi pi-spinner pi-spin" : ""}
                            />
                        </div>
                    </form>
                    <ul style={{ display: "flex", flexDirection: "column", gap: "7px", padding: "0px 0px 0px 20px", fontFamily: "var(--font-paragraph)", fontSize: "18px" }}>
                        <li>Password must be atleast 8 characters and include</li>
                        <li>Uppercase, Lowercase, Number, and a special character</li>
                    </ul>
                </div>
            )}
        </div >
    )
}

export default PasswordReset
